import { useMemo, useState } from 'react'
import { X, ChevronDown, ChevronUp } from 'lucide-react'

export interface OpenRouterMaxPrice {
  prompt?: number
  completion?: number
  request?: number
  image?: number
}

export type ReasoningEffort = 'minimal' | 'low' | 'medium' | 'high'

export interface OpenRouterReasoningConfig {
  enabled?: boolean
  effort?: ReasoningEffort
  maxTokens?: number
  exclude?: boolean
}

export interface OpenRouterConfig {
  order?: string[]
  only?: string[]
  ignore?: string[]
  quantizations?: string[]
  sort?: 'price' | 'latency' | 'throughput'
  dataCollection?: 'allow' | 'deny'
  requireParameters?: boolean
  allowFallbacks?: boolean
  zdr?: boolean
  preferredMinThroughput?: number
  preferredMaxLatency?: number
  maxPrice?: OpenRouterMaxPrice
  reasoning?: OpenRouterReasoningConfig
}

const COMMON_PROVIDERS = [
  'openai',
  'anthropic',
  'google-ai-studio',
  'google-vertex',
  'azure',
  'amazon-bedrock',
  'deepinfra',
  'together',
  'fireworks',
  'groq',
  'cerebras',
  'baseten',
  'novita',
  'lambda',
  'mistral',
  'xai',
  'cohere',
  'inflection',
  'perplexity',
  'sambanova',
  'hyperbolic',
  'lepton',
  'avian',
  'minimax',
  'nineteen',
]

const QUANTIZATION_OPTIONS = [
  'int4',
  'int8',
  'fp4',
  'fp6',
  'fp8',
  'fp16',
  'bf16',
  'fp32',
  'unknown',
]

interface ProviderChipsProps {
  value: string[]
  onChange: (next: string[]) => void
  suggestions: string[]
  placeholder?: string
  reorderable?: boolean
}

function ProviderChips({ value, onChange, suggestions, placeholder, reorderable }: ProviderChipsProps) {
  const [draft, setDraft] = useState('')

  const available = useMemo(
    () => suggestions.filter(s => !value.includes(s)),
    [suggestions, value]
  )

  const add = (slug: string) => {
    const trimmed = slug.trim().toLowerCase()
    if (!trimmed || value.includes(trimmed)) return
    onChange([...value, trimmed])
    setDraft('')
  }

  const remove = (slug: string) => onChange(value.filter(v => v !== slug))

  const move = (index: number, dir: -1 | 1) => {
    const next = [...value]
    const target = index + dir
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((slug, idx) => (
            <span
              key={slug}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs"
            >
              {reorderable && (
                <span className="flex items-center text-gray-400">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="rounded p-0.5 hover:bg-gray-200 disabled:opacity-30"
                  >
                    <ChevronUp className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === value.length - 1}
                    className="rounded p-0.5 hover:bg-gray-200 disabled:opacity-30"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </span>
              )}
              <span className="font-mono">{slug}</span>
              <button
                type="button"
                onClick={() => remove(slug)}
                className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add(draft)
            }
          }}
          placeholder={placeholder ?? 'Add provider slug…'}
          className="flex-1 min-w-[140px] rounded-md border border-gray-300 px-2 py-1 text-xs font-mono outline-none focus:border-gray-400"
        />
        <button
          type="button"
          onClick={() => add(draft)}
          disabled={!draft.trim()}
          className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-40"
        >
          Add
        </button>
      </div>
      {available.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {available.slice(0, 12).map(slug => (
            <button
              key={slug}
              type="button"
              onClick={() => add(slug)}
              className="rounded-md border border-dashed border-gray-300 px-1.5 py-0.5 text-[11px] font-mono text-gray-500 hover:border-gray-400 hover:text-gray-700"
            >
              + {slug}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

interface CheckboxRowProps {
  label: string
  description?: string
  checked: boolean
  onChange: (next: boolean) => void
}

function CheckboxRow({ label, description, checked, onChange }: CheckboxRowProps) {
  return (
    <label className="flex cursor-pointer items-start gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      <span className="flex-1">
        <span className="block text-sm font-medium text-gray-800">{label}</span>
        {description && <span className="block text-xs text-gray-500">{description}</span>}
      </span>
    </label>
  )
}

interface OpenRouterConfigPanelProps {
  value: OpenRouterConfig
  onChange: (next: OpenRouterConfig) => void
}

export function OpenRouterConfigPanel({ value, onChange }: OpenRouterConfigPanelProps) {
  const [showJson, setShowJson] = useState(false)

  const patch = (next: Partial<OpenRouterConfig>) => onChange({ ...value, ...next })

  const toggleQuant = (q: string) => {
    const current = value.quantizations ?? []
    const next = current.includes(q) ? current.filter(x => x !== q) : [...current, q]
    onChange({ ...value, quantizations: next.length > 0 ? next : undefined })
  }

  const updateMaxPrice = (key: keyof OpenRouterMaxPrice, raw: string) => {
    const num = raw === '' ? undefined : Number(raw)
    const nextMaxPrice = { ...(value.maxPrice ?? {}), [key]: Number.isFinite(num) ? num : undefined }
    const cleaned = Object.fromEntries(
      Object.entries(nextMaxPrice).filter(([, v]) => v !== undefined)
    ) as OpenRouterMaxPrice
    onChange({
      ...value,
      maxPrice: Object.keys(cleaned).length > 0 ? cleaned : undefined,
    })
  }

  const orderSuggestions = useMemo(
    () => (value.only && value.only.length > 0 ? value.only : COMMON_PROVIDERS),
    [value.only]
  )

  return (
    <div className="space-y-5 rounded-lg border border-gray-200 bg-gray-50 p-4 text-left">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">OpenRouter routing</h3>
        <p className="text-xs text-gray-500">
          Controls how OpenRouter picks an upstream provider for this model.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Sort by</label>
        <div className="flex flex-wrap gap-2">
          {(['none', 'price', 'throughput', 'latency'] as const).map(opt => {
            const active = opt === 'none' ? !value.sort : value.sort === opt
            return (
              <button
                key={opt}
                type="button"
                onClick={() =>
                  patch({ sort: opt === 'none' ? undefined : (opt as OpenRouterConfig['sort']) })
                }
                className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
                  active
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                {opt}
              </button>
            )
          })}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          `throughput` prioritises high-tps endpoints; disables OpenRouter load-balancing.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          Allowed providers <span className="text-gray-400">(only)</span>
        </label>
        <ProviderChips
          value={value.only ?? []}
          onChange={next => patch({ only: next.length > 0 ? next : undefined })}
          suggestions={COMMON_PROVIDERS}
          placeholder="e.g. anthropic, fireworks"
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          Preferred order <span className="text-gray-400">(order)</span>
        </label>
        <p className="mb-1 text-xs text-gray-500">
          Tried top-to-bottom. Drawn from `only` if set, otherwise any allowed provider.
        </p>
        <ProviderChips
          value={value.order ?? []}
          onChange={next => patch({ order: next.length > 0 ? next : undefined })}
          suggestions={orderSuggestions}
          placeholder="e.g. fireworks"
          reorderable
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          Ignored providers <span className="text-gray-400">(ignore)</span>
        </label>
        <ProviderChips
          value={value.ignore ?? []}
          onChange={next => patch({ ignore: next.length > 0 ? next : undefined })}
          suggestions={COMMON_PROVIDERS}
          placeholder="e.g. together"
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Quantizations</label>
        <div className="flex flex-wrap gap-1.5">
          {QUANTIZATION_OPTIONS.map(q => {
            const active = (value.quantizations ?? []).includes(q)
            return (
              <button
                key={q}
                type="button"
                onClick={() => toggleQuant(q)}
                className={`rounded-md border px-2 py-1 text-xs font-mono ${
                  active
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                {q}
              </button>
            )
          })}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Min throughput (tokens/sec)
          </label>
          <input
            type="number"
            min={0}
            step="any"
            value={value.preferredMinThroughput ?? ''}
            onChange={e => {
              const raw = e.target.value
              const num = raw === '' ? undefined : Number(raw)
              patch({ preferredMinThroughput: Number.isFinite(num) ? num : undefined })
            }}
            placeholder="e.g. 80"
            className="block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-gray-400"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Max latency (seconds)
          </label>
          <input
            type="number"
            min={0}
            step="any"
            value={value.preferredMaxLatency ?? ''}
            onChange={e => {
              const raw = e.target.value
              const num = raw === '' ? undefined : Number(raw)
              patch({ preferredMaxLatency: Number.isFinite(num) ? num : undefined })
            }}
            placeholder="e.g. 5"
            className="block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-gray-400"
          />
        </div>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          Max price <span className="text-gray-400">($ per 1M tokens)</span>
        </label>
        <div className="grid gap-2 sm:grid-cols-4">
          {(['prompt', 'completion', 'request', 'image'] as const).map(key => (
            <div key={key}>
              <label className="mb-0.5 block text-[11px] uppercase tracking-wide text-gray-500">
                {key}
              </label>
              <input
                type="number"
                min={0}
                step="any"
                value={value.maxPrice?.[key] ?? ''}
                onChange={e => updateMaxPrice(key, e.target.value)}
                placeholder=""
                className="block w-full rounded-md border border-gray-300 px-2 py-1 text-sm outline-none focus:border-gray-400"
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Data collection</label>
        <div className="flex gap-2">
          {(['default', 'allow', 'deny'] as const).map(opt => {
            const active = opt === 'default' ? !value.dataCollection : value.dataCollection === opt
            return (
              <button
                key={opt}
                type="button"
                onClick={() =>
                  patch({
                    dataCollection:
                      opt === 'default' ? undefined : (opt as OpenRouterConfig['dataCollection']),
                  })
                }
                className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
                  active
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                {opt}
              </button>
            )
          })}
        </div>
      </div>

      <div className="space-y-2 border-t border-gray-200 pt-3">
        <CheckboxRow
          label="Allow fallbacks"
          description="Let OpenRouter pick a backup provider when the preferred ones fail."
          checked={value.allowFallbacks ?? true}
          onChange={next => patch({ allowFallbacks: next })}
        />
        <CheckboxRow
          label="Require all parameters supported"
          description="Skip providers that don't support every field in the request (e.g. response_format, tools)."
          checked={value.requireParameters ?? false}
          onChange={next => patch({ requireParameters: next || undefined })}
        />
        <CheckboxRow
          label="Zero data retention only"
          description="Route only to endpoints that don't retain prompts or completions."
          checked={value.zdr ?? false}
          onChange={next => patch({ zdr: next || undefined })}
        />
      </div>

      <div className="border-t border-gray-200 pt-3">
        <label className="mb-1 block text-sm font-medium text-gray-700">Reasoning</label>
        <div className="mb-2">
          <CheckboxRow
            label="Disable reasoning (saves tokens; works on GLM 5.1, Z.AI models)"
            checked={value.reasoning?.enabled === false}
            onChange={next =>
              patch({
                reasoning: next
                  ? { enabled: false }
                  : value.reasoning
                    ? { ...value.reasoning, enabled: undefined }
                    : undefined,
              })
            }
          />
        </div>
        {value.reasoning?.enabled !== false && (
        <>
        <div className="flex flex-wrap gap-2">
          {(['off', 'minimal', 'low', 'medium', 'high'] as const).map(opt => {
            const active = opt === 'off' ? !value.reasoning?.effort : value.reasoning?.effort === opt
            return (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  if (opt === 'off') {
                    const stripped = value.reasoning
                      ? { exclude: value.reasoning.exclude }
                      : undefined
                    const hasAny = stripped && Object.values(stripped).some(v => v !== undefined)
                    patch({ reasoning: hasAny ? stripped : undefined })
                  } else {
                    patch({ reasoning: { ...value.reasoning, effort: opt, maxTokens: undefined } })
                  }
                }}
                className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
                  active
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                {opt}
              </button>
            )
          })}
        </div>
        {!value.reasoning?.effort && (
          <div className="mt-2">
            <label className="mb-0.5 block text-xs font-medium text-gray-600">Max reasoning tokens</label>
            <input
              type="number"
              min={0}
              step={1}
              value={value.reasoning?.maxTokens ?? ''}
              onChange={e => {
                const raw = e.target.value
                const num = raw === '' ? undefined : Number(raw)
                patch({
                  reasoning: {
                    ...value.reasoning,
                    maxTokens: Number.isFinite(num) ? num : undefined,
                  },
                })
              }}
              placeholder="e.g. 8000"
              className="block w-full max-w-[160px] rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-gray-400"
            />
          </div>
        )}
        <div className="mt-2">
          <CheckboxRow
            label="Exclude reasoning from response"
            checked={value.reasoning?.exclude ?? false}
            onChange={next =>
              patch({
                reasoning: next
                  ? { ...value.reasoning, exclude: true }
                  : { ...value.reasoning, exclude: undefined },
              })
            }
          />
        </div>
        </>
        )}
        <p className="mt-2 text-xs text-gray-500">
          GLM 5.1, Gemini, Anthropic, and OpenAI o-series models read this. Models that do not
          support reasoning ignore it silently. Check OR&apos;s /models supported_parameters list.
        </p>
      </div>

      <div className="border-t border-gray-200 pt-3">
        <button
          type="button"
          onClick={() => setShowJson(s => !s)}
          className="text-xs font-medium text-gray-500 hover:text-gray-700"
        >
          {showJson ? 'Hide' : 'Show'} raw JSON
        </button>
        {showJson && (
          <pre className="mt-2 max-h-48 overflow-auto rounded-md border border-gray-200 bg-white p-2 text-[11px] font-mono leading-snug">
            {JSON.stringify({ openrouter: value }, null, 2)}
          </pre>
        )}
      </div>
    </div>
  )
}

export function parseOpenRouterConfig(raw: unknown): OpenRouterConfig {
  if (!raw || typeof raw !== 'object') return {}
  const obj = raw as Record<string, unknown>
  const inner = (obj.openrouter ?? obj) as Record<string, unknown>
  const out: OpenRouterConfig = {}
  if (Array.isArray(inner.order)) out.order = inner.order.map(String)
  if (Array.isArray(inner.only)) out.only = inner.only.map(String)
  if (Array.isArray(inner.ignore)) out.ignore = inner.ignore.map(String)
  if (Array.isArray(inner.quantizations)) out.quantizations = inner.quantizations.map(String)
  if (inner.sort === 'price' || inner.sort === 'latency' || inner.sort === 'throughput') {
    out.sort = inner.sort
  }
  if (inner.dataCollection === 'allow' || inner.dataCollection === 'deny') {
    out.dataCollection = inner.dataCollection
  }
  if (typeof inner.requireParameters === 'boolean') out.requireParameters = inner.requireParameters
  if (typeof inner.allowFallbacks === 'boolean') out.allowFallbacks = inner.allowFallbacks
  if (typeof inner.zdr === 'boolean') out.zdr = inner.zdr
  if (typeof inner.preferredMinThroughput === 'number') {
    out.preferredMinThroughput = inner.preferredMinThroughput
  }
  if (typeof inner.preferredMaxLatency === 'number') {
    out.preferredMaxLatency = inner.preferredMaxLatency
  }
  if (inner.maxPrice && typeof inner.maxPrice === 'object') {
    const mp = inner.maxPrice as Record<string, unknown>
    const cleaned: OpenRouterMaxPrice = {}
    for (const key of ['prompt', 'completion', 'request', 'image'] as const) {
      if (typeof mp[key] === 'number') cleaned[key] = mp[key] as number
    }
    if (Object.keys(cleaned).length > 0) out.maxPrice = cleaned
  }
  if (inner.reasoning && typeof inner.reasoning === 'object') {
    const r = inner.reasoning as Record<string, unknown>
    const parsed: OpenRouterReasoningConfig = {}
    const validEfforts: ReasoningEffort[] = ['minimal', 'low', 'medium', 'high']
    if (typeof r.enabled === 'boolean') parsed.enabled = r.enabled
    if (validEfforts.includes(r.effort as ReasoningEffort)) parsed.effort = r.effort as ReasoningEffort
    if (typeof r.maxTokens === 'number') parsed.maxTokens = r.maxTokens
    if (typeof r.exclude === 'boolean') parsed.exclude = r.exclude
    if (Object.keys(parsed).length > 0) out.reasoning = parsed
  }
  return out
}

export function serializeOpenRouterConfig(value: OpenRouterConfig): Record<string, unknown> | undefined {
  const hasAny = Object.entries(value).some(([, v]) => {
    if (v === undefined) return false
    if (Array.isArray(v)) return v.length > 0
    if (typeof v === 'object') return Object.keys(v).length > 0
    return true
  })
  if (!hasAny) return undefined
  const cleaned: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value)) {
    if (v === undefined) continue
    if (Array.isArray(v) && v.length === 0) continue
    if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0) {
      continue
    }
    cleaned[k] = v
  }
  return { openrouter: cleaned }
}
